import type { LangCodeSchema } from '@sazeo/schemas';
import type { z } from 'zod';

export type LangCode = z.infer<typeof LangCodeSchema>;

/** Model aliases the product exposes. */
export type ClaudeModel = 'claude-sonnet-4-6' | 'claude-haiku-4-5' | 'claude-opus-4-7';

export type ClaudeScope =
  | 'read.people'
  | 'read.documents'
  | 'read.analytics'
  | 'read.messages'
  | 'write.tasks'
  | 'write.messages'
  | 'write.documents';

export interface ClaudeTool<Input = unknown, Output = unknown> {
  name: string;
  description: string;
  requiredScopes: readonly ClaudeScope[];
  approvalRequired: boolean;
  inputSchema: z.ZodType<Input>;
  run: (args: Input, ctx: ClaudeToolContext) => Promise<Output>;
}

export interface ClaudeToolContext {
  userId: string;
  tenantId: string;
  userLang: LangCode;
  userJwt: string;
  scopes: readonly ClaudeScope[];
}

/** Message turn in a Claude conversation (our abstraction, not Anthropic SDK types). */
export interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: Array<{
    toolName: string;
    input: unknown;
    output?: unknown;
    status: 'pending' | 'awaiting_approval' | 'approved' | 'rejected' | 'complete' | 'failed';
  }>;
}

export interface ClaudeChatRequest {
  messages: ClaudeMessage[];
  model?: ClaudeModel;
  allowedToolNames?: string[];
  stream?: boolean;
}

export interface ClaudeChatResponse {
  message: ClaudeMessage;
  usage: {
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  };
  quotaRemaining: number;
}

export interface ClaudeError {
  code:
    | 'QUOTA_EXCEEDED'
    | 'UNAUTHORIZED'
    | 'PROVIDER_FAILURE'
    | 'TOOL_FAILURE'
    | 'INVALID_INPUT'
    | 'INTERNAL';
  message: string;
  retryable: boolean;
}

export const isClaudeError = (v: unknown): v is ClaudeError =>
  typeof v === 'object' &&
  v !== null &&
  'code' in v &&
  typeof (v as ClaudeError).code === 'string' &&
  'retryable' in v;
